import { ModalService } from "../../../modules/modal/modal.service";
import { ModalDialogParams } from "../../../modules/modal/modals/modal.dialog.component";
import { EventFormOccurrenceUtil } from "../../s25-event-creation-form/occurrences/event.form.occurrence.util";
import type { EventForm } from "../../../standalone/event-form";
import { Event } from "../../../pojo/Event";
import { S25Event } from "../EventMicroI";
import { S25Reservation } from "../ReservationI";
import { EventData, EventProfile, EventService } from "../../../services/event.service";
import { LangService } from "../../../services/lang.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25Util } from "../../../util/s25-util";
import { S25ReservationUtil } from "./s25.reservation.util";

export type BoundEventLocationAction = "add" | "remove";
export type BoundEventLocationSurface = "EventForm" | "OccurrenceInlineEdit";

const FALLBACK_OCCURRENCE_MESSAGE =
    "Changing an occurrence on a bound event will also change all occurrences of its bound partner(s). Do you wish to continue?";

export const BOUND_LOCATION_ALERT_SUPPRESS_KEY = "s25-bound-location-alert-suppressed";

const bindingByEventProfile = new Map<string, boolean>();
const eventBindingsRequestByEventId = new Map<number, Promise<EventData | undefined>>();

export function getProfileReservationsForBoundLocationPrompt(
    event: S25Event | null | undefined,
    profileId: number | undefined,
): S25Reservation[] | undefined {
    const profile = event?.profile?.find((entry) => entry.itemId === profileId);
    return profile?.reservations as S25Reservation[] | undefined;
}

export type FormBoundOccurrence = {
    startDt: Date;
    endDt: Date;
    reservationState: number;
};

export type EventFormBoundLocationContext = Readonly<
    Partial<Pick<EventForm, "hasBinding" | "eventId" | "profileId" | "occurrences" | "boundOccurrences" | "getLocOccs">>
>;

function cacheKey(eventId: number, profileId?: number): string {
    return profileId != null ? `${eventId}:${profileId}` : `${eventId}:*`;
}

export function clearBoundEventBindingCache(eventId?: number, profileId?: number): void {
    if (eventId == null) {
        bindingByEventProfile.clear();
        eventBindingsRequestByEventId.clear();
        return;
    }
    bindingByEventProfile.delete(cacheKey(eventId, profileId));
    bindingByEventProfile.delete(cacheKey(eventId));
    eventBindingsRequestByEventId.delete(eventId);
}

function getEventBindingsCached(eventId: number): Promise<EventData | undefined> {
    let request = eventBindingsRequestByEventId.get(eventId);
    if (!request) {
        request = EventService.getEventBindings(eventId).catch((): undefined => undefined);
        eventBindingsRequestByEventId.set(eventId, request);
    }
    return request;
}

function profileHasBindings(profiles: EventProfile[], profileId?: number): boolean {
    if (profileId != null) {
        const profile = profiles.find((entry) => entry.profile_id === profileId);
        return !!profile?.binding_reservation?.length;
    }
    return profiles.some((entry) => !!entry.binding_reservation?.length);
}

function rememberBindingLookup(eventId: number, profileId: number | undefined, hasBindings: boolean): void {
    bindingByEventProfile.set(cacheKey(eventId, profileId), hasBindings);
}

export function clearBoundLocationAlertSuppression(): void {
    S25Util.localStorageRemove(BOUND_LOCATION_ALERT_SUPPRESS_KEY);
}

export function isBoundLocationAlertSuppressed(): boolean {
    return S25Util.toBool(S25Util.localStorageGet(BOUND_LOCATION_ALERT_SUPPRESS_KEY));
}

function locationIdSignature(locationIds: number[]): string {
    return [...locationIds].sort((left, right) => left - right).join(",");
}

export function profileReservationsHaveConsistentLocations(reservations: S25Reservation[] | undefined): boolean {
    const activeReservations = S25Util.array.forceArray(reservations);
    if (activeReservations.length <= 1) {
        return true;
    }

    const signatures = activeReservations.map((reservation) => {
        const locationItems = S25ReservationUtil.getObjectsFlat(reservation.locations?.[0]);
        const locationIds = locationItems
            .map((location) => location.itemId)
            .filter((itemId): itemId is number => itemId != null && Number.isFinite(itemId));
        return locationIdSignature(locationIds);
    });

    return locationSignaturesAreConsistent(signatures);
}

function locationSignaturesAreConsistent(signatures: string[]): boolean {
    if (signatures.length <= 1) {
        return true;
    }
    return signatures.every((signature) => signature === signatures[0]);
}

function isActiveS25ReservationForBoundCheck(reservation: S25Reservation): boolean {
    return reservation.state !== Event.Reservation.States.Cancelled;
}

/** True when a location add/remove targets fewer than all active profile reservations. */
export function isPartialOccurrenceInlineLocationChange(
    profileReservations: S25Reservation[] | undefined,
    affectedReservationIds: number[] | undefined,
): boolean {
    if (affectedReservationIds == null) {
        return false;
    }

    const activeReservations = S25Util.array
        .forceArray(profileReservations)
        .filter(isActiveS25ReservationForBoundCheck);
    if (activeReservations.length <= 1) {
        return false;
    }

    const activeIds = activeReservations
        .map((reservation) => reservation.itemId)
        .filter((itemId): itemId is number => itemId != null && Number.isFinite(itemId));
    const affectedIds = [
        ...new Set(
            affectedReservationIds.filter((itemId): itemId is number => itemId != null && Number.isFinite(itemId)),
        ),
    ];

    if (affectedIds.length < activeIds.length) {
        return true;
    }

    return !activeIds.every((itemId) => affectedIds.includes(itemId));
}

export function formProfileLocationsConsistent(formModel: EventFormBoundLocationContext): boolean {
    const occurrences = S25Util.array.forceArray(formModel.occurrences);
    if (occurrences.length <= 1) {
        return true;
    }

    const locationIdsByOcc = new Map<string, number[]>();
    for (const locOcc of formModel.getLocOccs?.() ?? []) {
        if (!locOcc.isIncluded) {
            continue;
        }
        const occKey = String(locOcc.occ?.uuid ?? locOcc.occ?.rsrvId ?? "");
        const locationIds = locationIdsByOcc.get(occKey) ?? [];
        locationIds.push(locOcc.item.itemId);
        locationIdsByOcc.set(occKey, locationIds);
    }

    const signatures = [...locationIdsByOcc.values()].map((locationIds) => locationIdSignature(locationIds));
    return locationSignaturesAreConsistent(signatures);
}

function reservationRangeSignature(startDt: Date, endDt: Date): string {
    return `${startDt.getTime()}:${endDt.getTime()}`;
}

function reservationSetsMatch(left: Set<string>, right: Set<string>): boolean {
    if (left.size !== right.size) {
        return false;
    }
    for (const signature of left) {
        if (!right.has(signature)) {
            return false;
        }
    }
    return true;
}

function isActiveFormOccurrenceForBoundCheck(occurrence: EventForm.Occurrence): boolean {
    const stateId = occurrence.state?.itemId;
    if (
        stateId != null &&
        [Event.State.Ids.Cancelled, Event.State.Ids.Confirmed].includes(stateId as Event.State.Ids)
    ) {
        return false;
    }
    return occurrence.includeInSearch !== false;
}

function isActiveBoundOccurrenceForBoundCheck(boundOccurrence: FormBoundOccurrence): boolean {
    return ![Event.State.Ids.Cancelled, Event.State.Ids.Confirmed].includes(
        boundOccurrence.reservationState as Event.State.Ids,
    );
}

function boundPartnerOccurrenceRangeSignatures(boundOccurrences: FormBoundOccurrence[]): Set<string> {
    const boundSignatures = new Set<string>();
    for (const boundOccurrence of S25Util.array.forceArray(boundOccurrences)) {
        if (!isActiveBoundOccurrenceForBoundCheck(boundOccurrence)) {
            continue;
        }
        boundSignatures.add(
            reservationRangeSignature(new Date(boundOccurrence.startDt), new Date(boundOccurrence.endDt)),
        );
    }
    return boundSignatures;
}

function boundPartnerReservationTimesAligned(
    primarySignatures: Set<string>,
    boundOccurrences: FormBoundOccurrence[],
): boolean {
    if (!S25Util.array.forceArray(boundOccurrences).length) {
        return true;
    }
    const boundSignatures = boundPartnerOccurrenceRangeSignatures(boundOccurrences);
    if (!boundSignatures.size) {
        return true;
    }
    return reservationSetsMatch(primarySignatures, boundSignatures);
}

/** Matches object-search availability: aligned when primary and partner check the same reservation windows. */
export function formBoundPartnerReservationTimesAligned(formModel: EventFormBoundLocationContext): boolean {
    const boundOccurrences = S25Util.array.forceArray(formModel.boundOccurrences);
    if (!boundOccurrences.length) {
        return true;
    }

    const primarySignatures = new Set<string>();
    for (const occurrence of S25Util.array.forceArray(formModel.occurrences)) {
        if (!occurrence || !isActiveFormOccurrenceForBoundCheck(occurrence)) {
            continue;
        }
        primarySignatures.add(
            reservationRangeSignature(
                EventFormOccurrenceUtil.getStartDt(occurrence),
                EventFormOccurrenceUtil.getEndDt(occurrence),
            ),
        );
    }

    return boundPartnerReservationTimesAligned(primarySignatures, boundOccurrences);
}

function reservationWindowForBoundCheck(reservation: S25Reservation): { startDt: Date; endDt: Date } | undefined {
    const window = S25ReservationUtil.getReservationsStartEndDates([reservation])[0];
    if (!window?.startDt || !window?.endDt) {
        return undefined;
    }
    return window;
}

/** Occurrence inline edit: aligned when profile and partner bound reservations share the same windows. */
export function profileBoundPartnerReservationTimesAligned(
    profileReservations: S25Reservation[] | undefined,
    boundOccurrences: FormBoundOccurrence[],
): boolean {
    const primarySignatures = new Set<string>();
    for (const reservation of S25Util.array.forceArray(profileReservations)) {
        if (!isActiveS25ReservationForBoundCheck(reservation)) {
            continue;
        }
        const window = reservationWindowForBoundCheck(reservation);
        if (!window) {
            continue;
        }
        primarySignatures.add(reservationRangeSignature(window.startDt, window.endDt));
    }

    return boundPartnerReservationTimesAligned(primarySignatures, boundOccurrences);
}

function bindingProfilesForPartnerLoad(eventProfiles: EventProfile[], profileId?: number): EventProfile[] {
    if (profileId != null) {
        const profile = eventProfiles.find((entry) => entry.profile_id === profileId);
        return profile?.binding_reservation?.length ? [profile] : [];
    }
    return eventProfiles.filter((entry) => entry.binding_reservation?.length);
}

function formBoundOccurrencesFromBindingProfile(profile: EventProfile | undefined): FormBoundOccurrence[] {
    return S25Util.array.forceArray(profile?.reservation).map((reservation) => ({
        startDt: S25Util.date.parse(reservation.reservation_start_dt),
        endDt: S25Util.date.parse(reservation.reservation_end_dt),
        reservationState: reservation.reservation_state,
    }));
}

async function loadBoundPartnerOccurrencesFromEventData(
    eventData: EventData | undefined,
    profileId?: number,
): Promise<FormBoundOccurrence[]> {
    const bindingProfiles = bindingProfilesForPartnerLoad(S25Util.array.forceArray(eventData?.profile), profileId);
    if (!bindingProfiles.length) {
        return [];
    }

    const boundOccurrenceGroups = await Promise.all(
        bindingProfiles.flatMap((profile) =>
            S25Util.array.forceArray(profile.binding_reservation).map(async (binding) => {
                const boundEvent = await getEventBindingsCached(binding.bound_event_id);
                const boundProfile = S25Util.array
                    .forceArray(boundEvent?.profile)
                    .find((entry) => entry.profile_id === binding.bound_reservation_id);
                return formBoundOccurrencesFromBindingProfile(boundProfile);
            }),
        ),
    );
    return boundOccurrenceGroups.flat();
}

async function resolveOccurrenceInlineBoundLocationContext(
    eventId: number,
    profileId?: number,
): Promise<{ isBound: boolean; boundPartnerOccurrences: FormBoundOccurrence[] }> {
    const eventData = await getEventBindingsCached(eventId);
    const profiles = S25Util.array.forceArray(eventData?.profile);
    const isBound = profileHasBindings(profiles, profileId);
    rememberBindingLookup(eventId, profileId, isBound);
    const boundPartnerOccurrences = isBound ? await loadBoundPartnerOccurrencesFromEventData(eventData, profileId) : [];
    return { isBound, boundPartnerOccurrences };
}

function shouldPromptBoundLocationAlert(
    isBound: boolean,
    options?: {
        formModel?: EventFormBoundLocationContext;
        profileReservations?: S25Reservation[];
        boundPartnerOccurrences?: FormBoundOccurrence[];
        affectedReservationIds?: number[];
    },
): boolean {
    if (!isBound) {
        return false;
    }
    if (isBoundLocationAlertSuppressed()) {
        return false;
    }
    if (options?.formModel) {
        const locationsConsistent = formProfileLocationsConsistent(options.formModel);
        const partnerTimesAligned = formBoundPartnerReservationTimesAligned(options.formModel);
        return !locationsConsistent || !partnerTimesAligned;
    }
    if (options?.profileReservations) {
        if (isPartialOccurrenceInlineLocationChange(options.profileReservations, options.affectedReservationIds)) {
            return true;
        }
        const locationsConsistent = profileReservationsHaveConsistentLocations(options.profileReservations);
        const partnerTimesAligned = profileBoundPartnerReservationTimesAligned(
            options.profileReservations,
            options.boundPartnerOccurrences ?? [],
        );
        return !locationsConsistent || !partnerTimesAligned;
    }
    return true;
}

async function boundLocationOccurrenceMessage(): Promise<string> {
    const lang = (await LangService.getLang()) as Record<string, any>;
    return (
        lang?.["s25-bind_reservations"]?.text?.bindings_occurrence_location_change?.content ??
        FALLBACK_OCCURRENCE_MESSAGE
    );
}

function telemetryEventType(action: BoundEventLocationAction, confirmed: boolean): string {
    if (action === "add") {
        return confirmed ? "BoundLocAddContinue" : "BoundLocAddCancel";
    }
    return confirmed ? "BoundLocRemoveContinue" : "BoundLocRemoveCancel";
}

function sendBoundLocationTelemetry(surface: BoundEventLocationSurface, eventType: string): void {
    if (surface === "EventForm") {
        TelemetryService.send("EventForm", eventType);
        return;
    }
    TelemetryService.sendWithSub("EventDetails", "EventDetailsOcc", eventType);
}

async function confirmBoundLocationWithOptionalDismiss(message: string): Promise<boolean> {
    const modalData = ModalService.dialogType("No Yes", { title: "Confirm", message }, "Yes") as ModalDialogParams & {
        showDontShowAgain?: boolean;
        dontShowAgain?: boolean;
    };
    modalData.showDontShowAgain = true;
    modalData.dontShowAgain = false;

    await ModalService.modal("dialog", modalData);
    const confirmed = modalData.answer === ModalDialogParams.Answer.Yes;
    if (confirmed && modalData.dontShowAgain) {
        S25Util.localStorageSet(BOUND_LOCATION_ALERT_SUPPRESS_KEY, "true");
    }
    return confirmed;
}

async function promptBoundLocationChange(
    action: BoundEventLocationAction,
    surface: BoundEventLocationSurface,
): Promise<boolean> {
    const confirmed = await confirmBoundLocationWithOptionalDismiss(await boundLocationOccurrenceMessage());
    sendBoundLocationTelemetry(surface, telemetryEventType(action, confirmed));
    return confirmed;
}

function normalizeEventFormBoundLocationContext(
    formModel: EventFormBoundLocationContext | boolean,
): EventFormBoundLocationContext {
    if (typeof formModel === "boolean") {
        return { hasBinding: formModel };
    }
    return formModel;
}

/** Profile-wide location add/remove on the event form. */
export async function confirmBoundEventLocationChangeForForm(
    action: BoundEventLocationAction,
    formModel: EventFormBoundLocationContext | boolean,
): Promise<boolean> {
    const context = normalizeEventFormBoundLocationContext(formModel);
    const isBound = await eventFormProfileIsBound(context);
    if (!shouldPromptBoundLocationAlert(isBound, { formModel: context })) {
        return true;
    }
    return promptBoundLocationChange(action, "EventForm");
}

async function eventFormProfileIsBound(formModel: EventFormBoundLocationContext): Promise<boolean> {
    if (formModel.hasBinding) {
        return true;
    }
    if (S25Util.array.forceArray(formModel.boundOccurrences).length) {
        return true;
    }
    // Event form: binding state comes from EventFormHydrant (edit/copy). New-event and
    // rose flows never hydrate bindings — do not call event.json here.
    return false;
}

/** Per-occurrence location changes on the event form (e.g. date/occurrences table). */
export async function confirmBoundEventLocationChangeForFormOccurrence(
    action: BoundEventLocationAction,
    formModel: EventFormBoundLocationContext,
): Promise<boolean> {
    const isBound = await eventFormProfileIsBound(formModel);
    if (!shouldPromptBoundLocationAlert(isBound, { formModel })) {
        return true;
    }
    return promptBoundLocationChange(action, "EventForm");
}

export async function eventProfileHasBindings(eventId: number, profileId?: number): Promise<boolean> {
    if (!eventId) return false;

    const key = cacheKey(eventId, profileId);
    if (bindingByEventProfile.has(key)) {
        return bindingByEventProfile.get(key)!;
    }

    const eventData = await getEventBindingsCached(eventId);
    const hasBindings = profileHasBindings(S25Util.array.forceArray(eventData?.profile), profileId);
    rememberBindingLookup(eventId, profileId, hasBindings);
    return hasBindings;
}

/** Occurrence inline edit helper — resolves binding state from the event API. */
export async function confirmBoundEventLocationChangeForOccurrence(
    action: BoundEventLocationAction,
    eventId: number,
    profileId?: number,
    profileReservations?: S25Reservation[],
    affectedReservationIds?: number[],
): Promise<boolean> {
    const { isBound, boundPartnerOccurrences } = await resolveOccurrenceInlineBoundLocationContext(eventId, profileId);
    if (
        !shouldPromptBoundLocationAlert(isBound, {
            profileReservations,
            boundPartnerOccurrences,
            affectedReservationIds,
        })
    ) {
        return true;
    }
    return promptBoundLocationChange(action, "OccurrenceInlineEdit");
}

/** AngJS bridge — event form uses model.hasBinding from hydrate. */
export const BoundEventLocationUtil = {
    confirmForForm: confirmBoundEventLocationChangeForForm,
    confirmForFormOccurrence: confirmBoundEventLocationChangeForFormOccurrence,
    confirmForOccurrence: confirmBoundEventLocationChangeForOccurrence,
    clearCache: clearBoundEventBindingCache,
};
